import { useEffect, useState } from "react"
import Papa from 'papaparse';
import moment from "moment";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import { Button, FileUpload, Modal } from "@prosolve/common-components-ui"
import { sessionsToEventsFormatter } from "./sessionEventUtils";
import useDataSessions from "../../dataHooks/useDataSessions";

const localizer = momentLocalizer(moment)

export default function UploadCsvSessionsModal({
  onClose,
  onSuccess,
}) {
  const [file, setFile] = useState()
  const [errors, setErrors] = useState([])
  const [sessions, setSessions] = useState([])
  const [events, setEvents] = useState([])
  const [date, setDate] = useState(moment().subtract(6, "months"))
  const [blueprintsMap, setBlueprintsMap] = useState()
  
  const isAddToCalendarEnabled = (sessions?.length > 0)

  const {
    fetchModuleBlueprints
  } = useDataSessions();

  const getEventBlueprints = async () => {
    const blueprints = await fetchModuleBlueprints();
    const blueprintsMap = blueprints.reduce((map,curr) => { 
      const key = curr.name.toLowerCase()
      if (!map[key]) map[key] = curr
      return map
    }, {})
    setBlueprintsMap(blueprintsMap)
  }

  const onFileChange = (file) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        setFile(file)

        const sessions = csvToSessions(results.data)
        setSessions(sessions)

        const calendarEvents = sessionsToEventsFormatter(sessions)
        calendarEvents.forEach((e,i) => e.id = `new-${i}`)
        setEvents(calendarEvents)

        if (calendarEvents.length > 0) {
          setDate(moment(calendarEvents[0].start).subtract(6, "months"))
        }
      },
      error: function(err, file, inputElem, reason) {
        console.log({ err, file, inputElem, reason })
      }
    })
  }

  const csvToSessions = (array) => {
    const errors = []

    const sessions = array.map((a,i) => {
      const hasData = Object.keys(a).some(k => a[k]?.length > 0)
      if (!hasData) return undefined

      // Validation
      let isValid = true;
      const checkValid = (isValidParam, message) => {
        if (!isValidParam) {
          isValid = isValidParam
          errors.push(['Row', i+2, message.join(': ')])
        }
      }

      const dateStr = a?.['Date']
      const date = moment(dateStr, ['M/D/YYYY', 'M/DD/YYYY', 'MM/D/YYYY', 'MM/DD/YYYY'], true); 
      checkValid(date.isValid(), ['invalid date, please use format MM/DD/YYYY', dateStr])

      const timeStartStr = a?.['Start Time']
      const timeStart = moment(timeStartStr, ['hA', 'hhA'], true); 
      checkValid(timeStart.isValid(), ['invalid start time', timeStartStr])

      const timeEndStr = a?.['End Time']
      const timeEnd = moment(timeEndStr, ['hA', 'hhA'], true); 
      checkValid(timeEnd.isValid(), ['invalid end time', timeEndStr])

      const programStr = a?.['Program']
      const blueprint = blueprintsMap?.[programStr?.toLowerCase()]
      checkValid(Boolean(blueprint), ['invalid program', programStr])

      // TODO should we only allow consultation sessions to add other fields?
      //isStarConsultation(eventData) 

      // Validate other fields?
      const schoolName = a?.['School Name']
      const schoolAddress = a?.['School Address']
      const teacherName = a?.['Teacher Name']
      const teacherEmail = a?.['Teacher Email']

      if (!isValid) return undefined

      // Translate
      return {
        // New session details
        date: date.format('YYYY-MM-DD'),
        startTime: timeStart.format('HH:mm:SS.SSS') + 'Z',
        endTime: timeEnd.format('HH:mm:SS.SSS') + 'Z',
        name: blueprint.name,
        image: blueprint.image,
        moduleBlueprintId: blueprint.id,
        // Edit session details
        schoolName: schoolName,
        address: schoolAddress,
        teacherName: teacherName,
        teacherEmail: teacherEmail
      }
    })

    setErrors(errors)

    return sessions.filter(s => s)
  }

  const onSubmit = () => {
    onSuccess(sessions)
    onClose()
  }

  useEffect(() => {
    getEventBlueprints()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const footer = (
    <div style={{ 
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      columnGap: '10px',
      padding: '10px'
    }}>
      <Button onClick={onClose}>
        {'Cancel'}
      </Button>
      <Button onClick={(isAddToCalendarEnabled ? onSubmit : undefined)} $colored={true} $disabled={!isAddToCalendarEnabled}>
        {'Add to Calendar'}
      </Button>
    </div>
  )

  return (
    <Modal title={'Upload Session CSV'} isAutoHeight={true} onClose={onClose} footer={footer}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '15px'
      }}>
        <div style={{
          display: 'flex',
          fontWeight: 'bold'
        }}>
          Upload
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <FileUpload onFileAttached={(file) => onFileChange(file)} />
        </div>

        {
          errors &&
          <div style={{
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              color: 'rgb(239, 12, 12)'
            }}>
              {errors.map(e => 
                <div key={e}>{e?.join(' ')}</div>
              )}
            </div>
          </div>
        }
        
        {
          Boolean(file) 
          &&
          <>
            <div style={{
              display: 'flex',
              fontWeight: 'bold'
            }}>
              Sessions
            </div>

            {
              events.length > 0
              ?
              <div style={{
                display: 'flex',
                justifyContent: 'center'
              }}>
                <Calendar
                  localizer={localizer}
                  events={events}
                  defaultView={Views.AGENDA}
                  date={date}
                  onNavigate={(newDate) => setDate(newDate)}
                  views={{ agenda: true }}
                  style={{ height: 500 }}
                  length={365}
                />
              </div>
              :
              <div style={{
                display: 'flex',
                justifyContent: 'center'
              }}>
                No sessions found
              </div>
            }
          </>
        }
      </div>
    </Modal>
  )
}